import { useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'
import { FaCircleCheck } from 'react-icons/fa6'

import { resetPassword } from '@/services/contribution/api.tsx'
import { useSchool } from '@/hooks'
import LoadingScreen from '@/components/LoadingScreen'
import SchoolLogo from '@/components/SchoolLogo'
import Section from '@components/Section'
import Button from '@components/Button'
import Input from '@components/Input'

export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const { schoolSlug } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const resetPasswordToken = searchParams.get('reset_password_token')

  const { data, isLoading } = useSchool(schoolSlug)

  const [password, setPassword] = useState('')
  const [touched, setTouched] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const passwordTooShort = password.length > 0 && password.length < 8
  const passwordTooLong = password.length > 16
  const passwordMissingNumber = password.length > 0 && !/\d/.test(password)
  const invalidPassword = passwordTooShort || passwordTooLong || passwordMissingNumber

  const resetPasswordMutation = useMutation({
    mutationFn: (password: string) => resetPassword(schoolSlug!, resetPasswordToken!, password),
    onSuccess: (success) => {
      if (success) {
        toast.success(t('content.components.reset_password.success_toast'))
        setShowConfirmation(true)
      } else {
        toast.error(t('content.components.reset_password.error_toast'), { autoClose: false })
      }
    },
    onError: (error) => {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: { location: 'Reset Password' },
          extra: { schoolSlug, errorMessage: error.message },
        })
      }
      toast.error(t('content.components.reset_password.error_toast'), { autoClose: false })
    },
  })

  function handleSubmit() {
    if (!password || !schoolSlug || !resetPasswordToken || invalidPassword) return
    resetPasswordMutation.mutate(password)
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!schoolSlug) {
    navigate('/')
    return null
  }

  const schoolLogo = data?.schoolLogo || undefined
  const schoolName = data?.schoolName || ''

  return (
    <>
      <title>Reset Password - Book Blast by Books Are Fun</title>
      <div className='w-full lg:w-3/4 mx-auto'>
        <div className='bg-cta text-center px-4 md:px-8'>
          <h1 className='text-white text-3xl font-serif text-center font-bold'>
            {t('content.components.reset_password.title')}
          </h1>
          <div className='text-white pt-2'>{t('content.components.reset_password.subtitle')}</div>
        </div>

        <Section wrapperStyle='my-8 justify-center items-center gap-4'>
          <SchoolLogo url={schoolLogo} />
          <div className='w-full text-center font-serif text-xl font-bold mt-4 mb-8'>{schoolName}</div>

          {!resetPasswordToken ? (
            <p className='text-center text-gray-700'>{t('content.components.reset_password.invalid_token_message')}</p>
          ) : showConfirmation ? (
            <div className='flex flex-col gap-4 justify-center items-center h-full text-center'>
              <div className='flex flex-row items-center gap-2'>
                <FaCircleCheck className='text-leaf-green h-6 w-6' />
                <h2 className='text-2xl font-serif font-semibold text-leaf-green'>
                  {t('content.components.reset_password.success_title')}
                </h2>
              </div>
              <p className='text-gray-700'>
                {t('content.components.reset_password.success_subtitle_before')}{' '}
                <Link to={`/${schoolSlug}/signin`} className='text-cta hover:text-cta-active hover:underline'>
                  {t('content.components.reset_password.success_subtitle_link')}
                </Link>{' '}
                {t('content.components.reset_password.success_subtitle_after')}
              </p>
              <Button
                variant='cta'
                label={t('content.components.signin.button_title')}
                fullwidth
                onClick={() => navigate(`/${schoolSlug}/signin`)}
              />
            </div>
          ) : (
            <div className='flex flex-col gap-4 justify-center items-center h-full w-full'>
              <p className='text-sm text-gray-500 w-full'>
                {t('content.components.reset_password.password_requirements')}
              </p>
              <div className='w-full'>
                <Input
                  type='password'
                  autocomplete='new-password'
                  label={t('content.components.reset_password.password_field_title')}
                  value={password}
                  onInput={setPassword}
                  placeholder={t('content.components.reset_password.password_field_placeholder')}
                  error={touched && invalidPassword}
                  onBlur={() => setTouched(true)}
                />
                {touched && passwordTooShort && (
                  <p className='mt-1 text-sm text-red-500'>
                    {t('content.components.registration.validation.password_too_short')}
                  </p>
                )}
                {touched && passwordTooLong && (
                  <p className='mt-1 text-sm text-red-500'>
                    {t('content.components.registration.validation.password_too_long')}
                  </p>
                )}
                {touched && !passwordTooShort && !passwordTooLong && passwordMissingNumber && (
                  <p className='mt-1 text-sm text-red-500'>
                    {t('content.components.registration.validation.password_missing_number')}
                  </p>
                )}
              </div>
              <Button
                variant='cta'
                label={t('content.components.reset_password.button_title')}
                fullwidth
                onClick={handleSubmit}
                disabled={resetPasswordMutation.isPending || invalidPassword}
              />
            </div>
          )}
        </Section>
      </div>
    </>
  )
}
